
import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ExternalLink, Save, AlertCircle, Info, ChevronDown } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import RecipeUrlForm from "@/components/RecipeUrlForm";

interface SearchResult {
  title: string;
  link: string;
  snippet: string;
}

interface SearchResultsProps {
  results: SearchResult[];
  isLoading: boolean;
  errorMessage: string | null;
  searchAttempted: boolean;
  onExtractRecipe: (url: string) => void;
  extractingUrl?: string;
  onRecipeAdded: () => void;
  onImportComplete: () => void;
  suggestionId?: string;
}

const SearchResults: React.FC<SearchResultsProps> = ({ 
  results, 
  isLoading, 
  errorMessage, 
  searchAttempted, 
  onExtractRecipe, 
  extractingUrl,
  onRecipeAdded,
  onImportComplete,
  suggestionId
}) => {
  const [visibleResults, setVisibleResults] = React.useState(5);
  
  // Helper function to extract and format domain from URL
  const extractDomain = (url: string): string => {
    try {
      const domain = new URL(url).hostname;
      return domain.replace(/^www\./, '');
    } catch (e) {
      return "";
    }
  };

  // Function to load more results
  const handleLoadMore = () => {
    setVisibleResults(prev => prev + 5);
  };

  // Display only the first `visibleResults` items
  const displayedResults = results.slice(0, visibleResults);
  const hasMoreResults = results.length > visibleResults;

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, i) => (
          <Card key={i}>
            <CardContent className="p-6 flex flex-col space-y-4">
              <Skeleton className="h-6 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <div className="flex justify-between">
                <Skeleton className="h-10 w-32" />
                <Skeleton className="h-10 w-32" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (errorMessage) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Search Error</AlertTitle>
        <AlertDescription>{errorMessage}</AlertDescription>
      </Alert>
    );
  }

  if (!searchAttempted) {
    return (
      <Alert className="mb-4">
        <Info className="h-4 w-4" />
        <AlertTitle>Find Your Next Recipe</AlertTitle>
        <AlertDescription>
          Enter what you want to cook or select ingredients you have on hand, then click "Find Recipes" to discover new dishes.
        </AlertDescription>
      </Alert>
    );
  }

  if (searchAttempted && results.length === 0) {
    return (
      <Alert variant="destructive" className="mb-4">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>No Recipes Found</AlertTitle>
        <AlertDescription>
          We couldn't find any recipes matching your search criteria from our trusted recipe sources. Try different search terms or ingredients.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <>
      {results.length > 0 && (
        <div className="bg-muted p-4 rounded-lg text-center mb-4">
          <p>Found {results.length} recipes matching your search</p>
        </div>
      )}
      
      <div className="space-y-4">
        {displayedResults.map((result, index) => (
          <Card key={index} className="overflow-hidden">
            <CardContent className="p-6 flex flex-col space-y-4">
              <div>
                <h3 className="text-xl font-semibold">{result.title}</h3>
                <p className="text-sm text-muted-foreground">{extractDomain(result.link)}</p>
              </div>
              {result.snippet && (
                <p className="text-muted-foreground text-sm">{result.snippet}</p>
              )}
              
              {/* Show inline import form if this recipe is being extracted */}
              {extractingUrl === result.link ? (
                <div className="border-t pt-4">
                  <RecipeUrlForm
                    onRecipeAdded={onRecipeAdded}
                    onImportComplete={onImportComplete}
                    initialUrl={result.link}
                    variant="inline"
                    suggestionId={suggestionId}
                  />
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2 sm:justify-between">
                  <Button 
                    variant="outline" 
                    onClick={() => window.open(result.link, '_blank')}
                    className="flex items-center"
                  >
                    <ExternalLink size={16} className="mr-2" />
                    View Recipe
                  </Button>
                  <Button
                    onClick={() => onExtractRecipe(result.link)}
                    className="flex items-center bg-recipe hover:bg-recipe-dark text-recipe-foreground"
                  >
                    <Save size={16} className="mr-2" />
                    Extract & Save
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
        
        {hasMoreResults && (
          <div className="mt-4 text-center">
            <Button 
              variant="outline" 
              onClick={handleLoadMore}
              className="flex items-center mx-auto"
            >
              <ChevronDown size={16} className="mr-2" />
              Load More Results ({results.length - visibleResults} remaining)
            </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default SearchResults;
